import { useState, useEffect, useCallback, memo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// Need to use Button and ButtonGroup from MUI for design consistency
import { Input, InputAdornment, Button, ButtonGroup } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import { GridRenderCellParams } from "@mui/x-data-grid";
import moment from "moment";
import { debounce, startCase } from "lodash";
import clsx from "clsx";
import { Switch, FormControl, Typography, Grid, IconButton, Stack } from "@financeable-com-au/financeable-ui";
import { TENANT_STATUS, TENANT_TYPES, DEFAULT_SORT_FIELD, ROUTES } from "@/constants";
import { saveValue, tenantStateSelector, getAllTenants, updateTenantStatus } from "@/store/slices/tenantSlice";
import { TenantRow, GetAllTenantsFnArgs, ITenantSlice, ITenantState } from "@/store/slices/types/tenantSliceTypes";
import { AppDispatch } from "@/store/store";
import { fCurrency } from "@/utils/formatNumber";
// @ts-expect-error: ignoring as it is existing js file
import DataGridTable from "../../dataGridTable/DataGridTable";

export const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  borderRemove: {
    "& fieldset": {
      border: "0px !important",
    },
  },
  margin: {
    margin: theme.spacing(1),
  },
  withoutLabel: {
    marginTop: theme.spacing(3),
  },
  textField: {
    width: "61ch",
  },
}));

const TenantList = memo(() => {
  const classes = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const pageSizeRef = useRef(10);
  const tenantStatusFilterRef = useRef<string>(TENANT_STATUS.active);
  const tenantTypeFilterRef = useRef<string>("All");
  const tenantState = useSelector<ITenantSlice, ITenantState>(tenantStateSelector);
  const { tenants, gettingTenants, page } = tenantState;
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [updatingCells, setUpdatingCells] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [rowCountState, setRowCountState] = useState(tenants?.totalDocs || 0);
  const [params, setParams] = useState({ offset: 1, limit: 10, sort: DEFAULT_SORT_FIELD } as {
    offset: number;
    limit: number;
    sort: { field: string; sort: "asc" | "desc" };
  });
  const tenantsStatusFilters = ["All", TENANT_STATUS.active, TENANT_STATUS.inactive];
  const tenantsTypeFilters = ["All", TENANT_TYPES.broker, TENANT_TYPES.lender];

  const handle = {
    navigateToTenantPage: (tenantId: string) => {
      navigate(ROUTES.ADMIN.TENANT_DETAIL.replace(":tenantId", String(tenantId)));
    },
    getAllTenantsFn: ({
      cursor = 1,
      search,
      filter = tenantStatusFilterRef.current || TENANT_STATUS.active,
      clientType = tenantTypeFilterRef.current || "All",
    }: GetAllTenantsFnArgs) => {
      const sort = params?.sort || DEFAULT_SORT_FIELD;
      dispatch(getAllTenants({ cursor, limit: pageSizeRef?.current || 10, search, filter, clientType, sort }));
    },
    handleSearch: (searchTerm: string) => {
      (searchTerm.length >= 2 || searchTerm.length <= 0) &&
        handle.getAllTenantsFn({ cursor: tenants?.page, search: searchTerm });
    },
    handleStatusFilterChange: async (option: string) => {
      handle.getAllTenantsFn({
        cursor: 1,
        search: searchTerm,
        filter: option,
      });
      tenantStatusFilterRef.current = option;
    },
    handleTenantTypeFilterChange: async (option: string) => {
      handle.getAllTenantsFn({
        cursor: 1,
        search: searchTerm,
        clientType: option,
      });
      tenantTypeFilterRef.current = option;
    },
    handleStatusToggle: async (field: string, tenantId: string, value: boolean) => {
      const cellKey = `${tenantId}:${field}`;
      // Guard against a second request for the same cell while one is in flight.
      if (updatingCells[cellKey]) return;
      setUpdatingCells((prev) => ({ ...prev, [cellKey]: true }));
      try {
        await dispatch(updateTenantStatus({ tenantId, field, value })).unwrap();
      } catch {
        // Request failed - the slice keeps the previous value, so the toggle reverts.
      } finally {
        setUpdatingCells((prev) => {
          const next = { ...prev };
          delete next[cellKey];
          return next;
        });
      }
    },
  };

  const renderStatusToggle =
    (field: "isActive" | "isInvoiceEnabled" | "isAutoInvoiceEnabled") => (params: GridRenderCellParams<boolean>) => {
      const tenantId = String(params.row.id);
      const isUpdating = !!updatingCells[`${tenantId}:${field}`];
      return (
        <Switch
          checked={!!params.value}
          variant={params.value ? "solid" : "outlined"}
          disabled={isUpdating}
          onChange={(event) => handle.handleStatusToggle(field, tenantId, event.target.checked)}
        />
      );
    };

  const tenantsColumns = [
    {
      field: "createdAt",
      headerName: "Date",
      type: "string",
      width: 100,
      editable: false,
      renderCell: (params: GridRenderCellParams<{ date: string; time: string }>) => (
        <div>
          <Typography style={{ fontSize: "14px" }}>{params.value?.date}</Typography>
          <Typography textColor="neutral.500" style={{ fontSize: "11px" }}>
            {params.value?.time}
          </Typography>
        </div>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 150,
    },
    {
      field: "email",
      headerName: "Email",
      width: 220,
    },
    {
      field: "billingName",
      headerName: "Billing Name",
      width: 220,
    },
    {
      field: "organisationName",
      headerName: "Organisation",
      width: 180,
    },
    {
      field: "clientType",
      headerName: "Tenant Type",
      width: 120,
    },

    {
      field: "licenceFees",
      headerName: "Licence Fees",
      width: 110,
      valueFormatter: (params: GridRenderCellParams<number>) => fCurrency(params.value || 0),
    },
    {
      field: "referralFees",
      headerName: "Referral Fees",
      width: 110,
      valueFormatter: (params: GridRenderCellParams<number>) => fCurrency(params.value || 0),
    },
    {
      field: "isActive",
      headerName: "Active",
      width: 100,
      sortable: false,
      renderCell: renderStatusToggle("isActive"),
    },
    {
      field: "isInvoiceEnabled",
      headerName: "Invoice Enabled",
      width: 130,
      sortable: false,
      renderCell: renderStatusToggle("isInvoiceEnabled"),
    },
    {
      field: "isAutoInvoiceEnabled",
      headerName: "Auto Invoice Enabled",
      width: 130,
      sortable: false,
      renderCell: renderStatusToggle("isAutoInvoiceEnabled"),
    },
    {
      field: "workOSOrgId",
      headerName: "WorkOS Org Id",
      width: 320,
    },
    {
      field: "action",
      headerName: "Action",
      width: 220,
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<unknown, TenantRow>) => (
        <Stack direction="row" spacing={0.5}>
          <Button
            color="primary"
            size="large"
            disableFocusRipple
            tabIndex={-1}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              handle.navigateToTenantPage(String(params.row.id));
            }}
            sx={{ "&:focus": { outline: "none" }, "&.Mui-focusVisible": { outline: "none" } }}
          >
            <EditRoundedIcon />
          </Button>
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    setRowCountState((prevRowCountState) => tenants?.totalDocs ?? prevRowCountState);
  }, [tenants?.totalDocs]);

  useEffect(() => {
    if (Array.isArray(tenants?.list)) {
      const tenantsRows = tenants.list.map((doc) => {
        const createdAt = doc?.createdAt ? new Date(doc?.createdAt) : "";
        const fullName = [doc?.firstName, doc?.lastName].filter(Boolean).join(" ");
        const connection = doc?.clientConnection;
        return {
          id: doc?._id,
          name: fullName || "-",
          email: doc?.email || "-",
          billingName: doc?.billingName || "-",
          organisationName: connection?.name || "-",
          cursor: tenants?.page,
          workOSOrgId: connection?.workOSOrgId || "-",
          clientType: connection?.clientType || "-",
          licenceFees: doc?.licenceFees ?? 0,
          referralFees: doc?.referralFees ?? 0,
          isActive: connection?.isActive || false,
          isInvoiceEnabled: connection?.isInvoiceEnabled || false,
          isAutoInvoiceEnabled: connection?.isAutoInvoiceEnabled || false,
          createdAt: createdAt
            ? {
                date: moment(createdAt).format("D-M-YY"),
                time: moment(createdAt).format("h:mm a"),
              }
            : {
                date: "",
                time: "",
              },
        };
      });

      setRows(tenantsRows);
    } else {
      setRows([]);
    }
  }, [tenants]);

  useEffect(() => {
    if (params?.offset) {
      handle.getAllTenantsFn({
        cursor: params?.offset,
        search: searchTerm,
        filter: tenantStatusFilterRef.current,
      });
      dispatch(saveValue({ page: params?.offset, limit: params?.limit }));
    }
  }, [params?.offset, params?.sort, params?.limit]);

  const debounced = useCallback(debounce(handle.handleSearch, 500), []);

  return (
    <Grid container direction="column" spacing={0} style={{ padding: `20px` }}>
      <Grid
        style={{
          borderBottom: "1px solid rgba(224, 224, 224, 1)",
          display: "flex",
          alignItems: "center",
          columnGap: "50px",
        }}
      >
        <Typography
          level="h1"
          style={{
            fontSize: "24px",
            fontWeight: 800,
            letterSpacing: "-0.5px",
          }}
        >
          Tenants
        </Typography>
        <div className={classes.root}>
          <FormControl className={clsx(classes.margin, classes.textField)}>
            <Input
              className="input-border-bottom"
              id="tenant-search"
              autoComplete="search"
              placeholder="Search…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                debounced(e.target.value);
              }}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchTerm("");
                        searchTerm !== "" && debounced("");
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            />
          </FormControl>
        </div>
        <ButtonGroup variant="outlined">
          {tenantsStatusFilters.map((option) => (
            <Button
              key={option}
              size="small"
              onClick={() => {
                handle.handleStatusFilterChange(option);
              }}
              variant={tenantStatusFilterRef.current === option ? "contained" : "outlined"}
            >
              {startCase(option)}
            </Button>
          ))}
        </ButtonGroup>
        <ButtonGroup variant="outlined">
          {tenantsTypeFilters.map((option) => (
            <Button
              key={option}
              size="small"
              onClick={() => {
                handle.handleTenantTypeFilterChange(option);
              }}
              variant={tenantTypeFilterRef.current === option ? "contained" : "outlined"}
            >
              {startCase(option)}
            </Button>
          ))}
        </ButtonGroup>
      </Grid>

      <Grid>
        <DataGridTable
          onCellClick={(params: { field: string; id: string }) => {
            const nonNavigableFields = ["isActive", "isInvoiceEnabled", "isAutoInvoiceEnabled", "action"];
            if (nonNavigableFields.includes(params.field)) return;
            handle.navigateToTenantPage(params.id);
          }}
          onRowClick={() => {}}
          data={rows || []}
          columns={tenantsColumns}
          rowsPerPageOptions={[10, 20, 50, 100]}
          pageSize={pageSizeRef?.current || 10}
          rowCount={rowCountState}
          params={params}
          setParams={setParams}
          page={page}
          isLoading={gettingTenants}
          pageSizeRef={pageSizeRef}
          disableColumnFilter
        />
      </Grid>
    </Grid>
  );
});

export default TenantList;
